/// <amd-module name="VSSUI/Components/Button/Button" />
import * as React from "react";
import { FocusGroupContext } from "VSSUI/FocusGroup";
import { FocusZoneContext } from "VSSUI/FocusZone";
import { Icon, IconSize } from "VSSUI/Icon";
import { Tooltip } from "VSSUI/TooltipEx";
import { childCount, css, getSafeId, KeyCode } from "VSSUI/Util";
import { getTabIndex, IFocusable } from "VSSUI/Utilities/Focus";
import { IButtonProps } from "./Button.Props";

export class Button extends React.Component<IButtonProps> implements IFocusable<{}> {
    public static contextType = FocusGroupContext;

    private buttonElement: React.RefObject<HTMLButtonElement & HTMLAnchorElement> = React.createRef();

    public render(): JSX.Element {
        if ($DEBUG) {
            if (this.props.danger && this.props.primary) {
                console.warn("primary and danger props are both set to true on Button, only one should be set to true at a time.");
            }
        }

        // Determine if the button is an iconOnly button.
        const iconOnly = this.props.iconProps && !this.props.text && childCount(this.props.children) === 0;
        const tooltipProps =
            this.props.tooltipProps !== undefined
                ? this.props.tooltipProps
                : iconOnly && this.props.ariaLabel
                ? { text: this.props.ariaLabel }
                : undefined;

        return (
            <FocusZoneContext.Consumer>
                {(zoneContext): JSX.Element => {
                    const ButtonType = this.props.href ? "a" : "button";

                    // @TODO (line-height): remove the body-m from the text once the line-height is applied globally.
                    let role = this.props.role || (this.props.href ? "link" : "button");
                    let button = (
                        // @ts-ignore TypeScript no longer works with dynamic intrinsic component types.
                        <ButtonType
                            autoFocus={!this.props.href ? this.props.autoFocus : undefined}
                            aria-controls={getSafeId(this.props.ariaControls)}
                            aria-describedby={getSafeId(this.props.ariaDescribedBy)}
                            aria-disabled={this.props.disabled || this.props.ariaDisabled}
                            aria-expanded={this.props.ariaExpanded}
                            aria-haspopup={this.props.ariaHasPopup}
                            aria-hidden={this.props.ariaHidden}
                            aria-label={this.props.ariaLabel}
                            aria-labelledby={this.props.ariaLabelledBy}
                            aria-setsize={this.props.ariaSetSize}
                            aria-posinset={this.props.ariaPosInSet}
                            aria-selected={this.props.ariaSelected}
                            aria-checked={this.props.ariaChecked}
                            aria-pressed={this.props.ariaPressed}
                            aria-roledescription={this.props.ariaRoleDescription}
                            className={css(
                                this.props.className,
                                "bolt-button",
                                this.props.href && "bolt-link-button",
                                this.props.iconProps && "bolt-icon-button",
                                this.props.danger && "danger",
                                this.props.disabled ? "disabled" : "enabled",
                                this.props.primary && "primary",
                                this.props.subtle && "subtle",
                                iconOnly && "icon-only",
                                "bolt-focus-treatment"
                            )}
                            data-focuszone={
                                !this.props.disabled && !this.props.excludeFocusZone
                                    ? css(this.props.focusZoneId, zoneContext.focuszoneId)
                                    : undefined
                            }
                            data-index={this.props.dataIndex}
                            data-is-focusable={!this.props.excludeFocusZone}
                            href={!this.props.disabled ? this.props.href : undefined}
                            id={getSafeId(this.props.id)}
                            onBlur={this.props.onBlur}
                            onClick={this.onClick}
                            onMouseLeave={this.onMouseLeave}
                            onMouseOver={this.onMouseOver}
                            onKeyDown={this.onKeyDown}
                            onMouseDown={this.onMouseDown}
                            onFocus={this.onFocus}
                            rel={this.props.rel}
                            role={role}
                            style={this.props.style}
                            tabIndex={getTabIndex(this.props, this.context)}
                            target={this.props.target}
                            type={this.props.type ? this.props.type : !this.props.href ? "button" : undefined}
                            ref={this.buttonElement}
                        >
                            {this.props.iconProps &&
                                Icon({ size: IconSize.medium, ...this.props.iconProps, className: css(this.props.iconProps.className, "left-icon") })}
                            {this.props.text && <span className="bolt-button-text body-m">{this.props.text}</span>}
                            {this.props.children}
                        </ButtonType>
                    );

                    if (tooltipProps) {
                        button = (
                            <Tooltip addAriaDescribedBy={true} {...tooltipProps}>
                                {button}
                            </Tooltip>
                        );
                    }

                    return button;
                }}
            </FocusZoneContext.Consumer>
        );
    }

    public focus(): void {
        if (this.buttonElement.current) {
            this.buttonElement.current.focus();
        }
    }

    private onClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!this.props.disabled) {
            const { onClick } = this.props;

            // @NOTE: Safari doesnt set focus to buttons when they are clicked, we need this
            //  to help manage the focus state for callouts.
            this.focus();

            if (onClick) {
                onClick(event);
            }
        } else {
            event.preventDefault();
        }
    };

    private onFocus = (event: React.FocusEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
        if (this.props.id) {
            // @NOTE: Due to test issues using React.16.3.2 we MUST validate the onFocus method.
            this.context.onFocus && this.context.onFocus(this.props.id);
        }
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented && !this.props.disabled) {
            if (event.which === KeyCode.enter || event.which === KeyCode.space) {
                if (this.props.onClick) {
                    this.props.onClick(event);
                }
                if (!this.props.href) {
                    event.preventDefault();
                }
            } else if (this.props.onKeyDown) {
                this.props.onKeyDown(event);
            }
        }
    };

    private onMouseDown = (event: React.MouseEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (this.props.disabled) {
                event.preventDefault();
            }
        }
        const { onMouseDown } = this.props;
        if (onMouseDown) {
            onMouseDown(event);
        }
    };

    private onMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
        if (!this.props.disabled) {
            const { onMouseLeave } = this.props;

            if (onMouseLeave) {
                onMouseLeave(event);
            }
        }
    };

    private onMouseOver = (event: React.MouseEvent<HTMLElement>) => {
        if (!this.props.disabled) {
            const { onMouseOver } = this.props;

            if (onMouseOver) {
                onMouseOver(event);
            }
        }
    };
}
